import { infiniteQueryOptions, type QueryKey } from "@tanstack/react-query";

import { SOURCE_CACHE_LIFETIME } from "@/hooks/source-cache-lifetime";
import type { SpecLinesNextPage, SpecLinesPage, SpecLinesPageParam } from "@/hooks/specifications";

// Apart from `@/hooks/specifications`, whose module loads the signed-in reads'
// server functions: the share hooks (`@/hooks/share-public`), which the
// latest-version resolver's page loads too, reach the paging without them
// (#1024). The details page itself loads them anyway, through the lines loader.

/** One lines page's request: the page parameter read out, and the term when there is one. */
export type SpecLinesPageRequest = Omit<SpecLinesNextPage, "heldLineIds"> & {
  heldLineIds?: number[];
  search?: string;
};

/**
 * The lines' paging (`specificationLinesQueryOptions`), over any read that
 * answers a lines page under a key of its own — the signed-in read, and the
 * public page's token-scoped one (#1024), so the two never page differently.
 * `keyOf` is the read's key for a term.
 */
export function specLinesPagingOptions(
  keyOf: (search: string) => QueryKey,
  search: string,
  readPage: (request: SpecLinesPageRequest) => Promise<SpecLinesPage | null>,
) {
  return infiniteQueryOptions({
    queryKey: keyOf(search),
    queryFn: ({ pageParam }) =>
      readPage({
        ...(typeof pageParam === "number" ? { cursor: pageParam } : pageParam),
        ...(search ? { search } : {}),
      }),
    initialPageParam: 0 as SpecLinesPageParam,
    getNextPageParam: (last, pages): SpecLinesPageParam | undefined =>
      last !== null && last.hasMore
        ? {
            cursor: last.cursor,
            heldLineIds: pages.flatMap((page) => page?.lines.map((line) => line.id) ?? []),
            ...(search ? { snapshotGroupIds: last.groups.map((group) => group.id) } : {}),
          }
        : undefined,
    ...SOURCE_CACHE_LIFETIME,
  });
}
